/**
 * Per-event image resolution for the WasteSense report.
 *
 * The fixture currently ships with empty `image` fields on events. The
 * production report will eventually return either a signed object-store
 * URL or a base64 data URI. Both have very different lifecycles (one is
 * trivially cacheable, one bloats every payload), so consumers go via
 * this small interface rather than reading `row.image` directly.
 *
 * The default implementation here points at `placehold.co` so the UI
 * has something to render while the real fetcher is wired up. Swap in
 * a real `ImageProvider` (e.g. one that calls a signed-URL API) later
 * without touching the components.
 */
import { formatEventTimestamp } from "./components/eventDisplay";
import { formatSiteHeading } from "./siteDisplay";
import type { WasteSenseEventRow, WasteSenseSite } from "./types";

/**
 * Optional context the call site can pass alongside the row. The
 * placeholder provider uses it to render a richer label on the swatch
 * (`<camera>`, `<address>`, `<timestamp>`); production providers can
 * use it to choose the right signing-key scope or cache shard.
 *
 * Optional because the original signature took only the row, and the
 * snapshot-lightbox callsite resolves URLs without site context (it
 * already has the URL it opened with).
 */
export interface ImageContext {
  /** The site that owns the row. Used for the placeholder address line. */
  site?: WasteSenseSite;
}

export interface ImageProvider {
  /**
   * Resolve a fully-qualified image URL for a single event.
   *
   * Returns `null` when no image is available for this event — callers
   * should hide the thumbnail rather than render a broken image.
   */
  getEventImageUrl(row: WasteSenseEventRow, context?: ImageContext): string | null;
}

interface PlaceholderProviderOptions {
  /** Width in pixels, capped at 4000 by placehold.co. */
  width?: number;
  /** Height in pixels, capped at 4000 by placehold.co. Defaults to a 4:3 aspect. */
  height?: number;
}

/**
 * Returns a placehold.co URL keyed off the event's activity tag and
 * camera. Same activity → same colour, so visually similar rows cluster.
 *
 * The label printed on the swatch is `<camera> / <address> / <timestamp>`
 * when site context is available, and falls back to `<camera> / <activity>`
 * for callsites that don't pass one (e.g. the lightbox, which only
 * receives the row).
 *
 * Format reference: https://placehold.co/{w}x{h}/{bg}/{fg}/png?text=...
 */
export function placeholderImageProvider(opts: PlaceholderProviderOptions = {}): ImageProvider {
  // 800x600 keeps a 4:3 aspect (matching the production camera frame)
  // and is large enough that the lightbox renders a crisp source —
  // contentFit="contain" was previously upscaling a 320x240 swatch,
  // which made demos look pixelated. Well under placehold.co's 4000px
  // cap and the URL is still short enough to be trivially cacheable.
  const width = opts.width ?? 800;
  const height = opts.height ?? 600;

  return {
    getEventImageUrl(row, context) {
      const activity = (row.activity_type || "Event").trim();
      const palette = pickPalette(activity);
      const cameraLabel = row.camera || row.asset_id;
      const site = context?.site;

      // placehold.co treats `\n` (URL-encoded `%0A`) as a hard line
      // break in the rendered label, so we build the multi-line label
      // before encoding the whole thing.
      const lines =
        site ?
          [cameraLabel, formatSiteHeading(site.title), formatEventTimestamp(row.timestamp, site.timezone)]
        : [cameraLabel, activity];
      const text = encodeURIComponent(lines.join("\n"));
      return `https://placehold.co/${width}x${height}/${palette.bg}/${palette.fg}/png?text=${text}`;
    },
  };
}

interface PlaceholderPalette {
  /** Background hex (no `#`). */
  bg: string;
  /** Foreground hex (no `#`). */
  fg: string;
}

/**
 * Stable palette picker — hashes the input and indexes into a small
 * curated list. Avoids the visual noise of random colours per render
 * while still differentiating activity types at a glance.
 */
function pickPalette(seed: string): PlaceholderPalette {
  const palettes: readonly PlaceholderPalette[] = [
    { bg: "0F172A", fg: "F8FAFC" }, // slate
    { bg: "1E3A8A", fg: "DBEAFE" }, // navy
    { bg: "065F46", fg: "D1FAE5" }, // teal
    { bg: "78350F", fg: "FEF3C7" }, // amber
    { bg: "7C2D12", fg: "FED7AA" }, // burnt
    { bg: "4C1D95", fg: "EDE9FE" }, // violet
    { bg: "881337", fg: "FFE4E6" }, // rose
    { bg: "374151", fg: "F3F4F6" }, // grey
  ];

  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  }
  const index = Math.abs(hash) % palettes.length;
  return palettes[index] ?? palettes[0]!;
}

/** Default provider used by the screen until a real one is wired in. */
export const defaultImageProvider: ImageProvider = placeholderImageProvider();
